import type { ApiError } from '@/types'
import axios, { type AxiosInstance, type AxiosError, type InternalAxiosRequestConfig } from 'axios'

/** Turn FastAPI `detail` (string | validation object[]) into a single readable string. */
function formatFastApiDetail(detail: unknown): string {
  if (detail == null) return ''
  if (typeof detail === 'string') return detail
  if (Array.isArray(detail)) {
    return detail
      .map((item) => {
        if (item && typeof item === 'object' && 'msg' in item) {
          return String((item as { msg: unknown }).msg)
        }
        return JSON.stringify(item)
      })
      .filter(Boolean)
      .join('; ')
  }
  if (typeof detail === 'object' && 'msg' in (detail as object)) {
    return String((detail as { msg: unknown }).msg)
  }
  return String(detail)
}

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000'
const API_TIMEOUT = Number(import.meta.env.VITE_API_TIMEOUT) || 30000

console.log('[api-client] VITE_API_URL:', import.meta.env.VITE_API_URL, '-> using:', API_URL)

export const apiClient: AxiosInstance = axios.create({
  baseURL: API_URL,
  timeout: API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor - Add auth token
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('auth_token')
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  async (error) => {
    return await Promise.reject(error)
  },
)

// Response interceptor - Handle errors
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiError>) => {
    const requestUrl = error.config?.url || ''
    const isAuthEndpoint = requestUrl.includes('/auth/')

    // Only redirect on 401 for non-auth endpoints (session expired)
    // Auth endpoints (login, OTP verification) should handle 401 themselves
    if (error.response?.status === 401 && !isAuthEndpoint) {
      // Clear all auth data (both token and Zustand storage) and redirect to login
      localStorage.removeItem('auth_token')
      localStorage.removeItem('auth-storage')
      if (window.location.pathname !== '/login') {
        window.location.href = '/login'
      }
    }

    // FastAPI returns errors in 'detail' field, some APIs use 'message'
    // biome-ignore lint/suspicious/noExplicitAny: FastAPI error response has 'detail' not in ApiError type
    const responseData = error.response?.data as any
    const fromDetail = formatFastApiDetail(responseData?.detail)
    const errorMessage =
      fromDetail ||
      (typeof responseData?.message === 'string' ? responseData.message : '') ||
      error.message ||
      'An error occurred'

    const apiError: ApiError = {
      message: errorMessage,
      statusCode: error.response?.status || 500,
      ...(responseData?.errors && { errors: responseData.errors }),
    }

    return await Promise.reject(apiError)
  },
)

export default apiClient
